import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Divider,
    InlineStack,
    Layout,
    Modal,
    Page,
    ProgressBar,
    Select,
    Spinner,
    Text,
    Thumbnail,
} from '@shopify/polaris';
import { ImageIcon, RefreshIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import agent from '../../app/api/agent';
import {
    AtdLifecycleStep,
    AtdShipToHomeCancelEligibilityResult,
    AtdShipToHomeOrderDetail,
    AtdShipToHomeOrderEventDetail,
    AtdShipToHomeOrderLineDetail,
} from '../../app/models/Atd';
import { getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';

const eventPreviewCount = 6;
type ConfirmationAction = 'refreshPreview' | 'submitOrder' | 'archive';

const formatDateTime = (value?: string) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-';
const formatMoney = (value?: number) => value === undefined || value === null ? '-' : `$${value.toFixed(2)}`;
const hasText = (value?: string | null) => Boolean(value && value.trim().length > 0);

const getErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) {
        return data;
    }

    if (data && typeof data === 'object') {
        return JSON.stringify(data);
    }

    return error?.message || fallback;
};

const currentErrorStatuses = new Set([
    'failed',
    'previewfailed',
    'statussyncfailed',
    'shopifyfulfillmentfailed',
]);

const shipmentStatuses = new Set([
    'submitted',
    'waitingforshipment',
    'waitingforstoredelivery',
    'partiallyshipped',
    'shipped',
    'statussyncing',
]);

const readyForPickupStatuses = new Set([
    'readyforpickup',
]);

const previewStatuses = new Set([
    'pendingpreview',
    'previewing',
    'previewvalid',
]);

const hiddenTimelineEventTypes = new Set([
    'previewstarted',
    'submissionstarted',
    'statussyncstarted',
    'statussynccompleted',
    'statussyncfailed',
    'canceleligibilitystarted',
    'cancelstarted',
]);

const normalizeStatus = (value?: string) => (value || '').trim().toLowerCase();
const isShipToStoreOrder = (order: AtdShipToHomeOrderDetail) => normalizeStatus(order.fulfillmentType) === 'shiptostore';
const isPickupDeliveryOrder = (order: AtdShipToHomeOrderDetail) => normalizeStatus(order.shopifyDeliveryMethodType).includes('pick');
const isCancelledOrder = (order: AtdShipToHomeOrderDetail) => Boolean(order.cancelledAt) || normalizeStatus(order.status) === 'cancelled';
const isPreviewApprovalPending = (order: AtdShipToHomeOrderDetail) => order.canSubmit;
const formatFulfillmentType = (order: AtdShipToHomeOrderDetail) => isShipToStoreOrder(order) ? 'Ship to store' : 'Ship to home';
const getFulfillmentTypeOptions = (order: AtdShipToHomeOrderDetail) => [
    {
        label: isPickupDeliveryOrder(order) ? 'Ship to home (requires shipping address)' : 'Ship to home',
        value: 'ShipToHome',
        disabled: isPickupDeliveryOrder(order),
    },
    { label: 'Ship to store', value: 'ShipToStore' },
];
const formatShipToStoreOptions = (order: AtdShipToHomeOrderDetail) => [
    order.atdPickup === true ? 'ATD pickup' : 'Deliver to store',
    order.atdLocalPlus === true ? 'Local Plus' : undefined,
    order.atdQuickShip === true ? 'Quick Ship' : undefined,
    order.atdFillKillOption ? `Fill/kill ${order.atdFillKillOption}` : undefined,
    order.atdOrderType ? `Order type ${order.atdOrderType}` : undefined,
].filter(Boolean).join(' | ');

const detailTextStyle: React.CSSProperties = {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
};

const statusBadgeWrapStyle: React.CSSProperties = {
    display: 'inline-flex',
    width: 'fit-content',
};

const detailRowStyle: React.CSSProperties = {
    display: 'grid',
    gap: 12,
    gridTemplateColumns: 'minmax(120px, 45%) minmax(0, 1fr)',
};

const detailValueStyle: React.CSSProperties = {
    overflowWrap: 'anywhere',
    textAlign: 'right',
};

const financialSummaryGridStyle: React.CSSProperties = {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
};

const previewHeaderRowStyle: React.CSSProperties = {
    alignItems: 'center',
    display: 'flex',
    gap: 16,
    justifyContent: 'space-between',
    width: '100%',
};

const productRowStyle: React.CSSProperties = {
    alignItems: 'center',
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'auto minmax(0, 1fr) minmax(112px, auto)',
};

const productTotalsStyle: React.CSSProperties = {
    minWidth: 112,
    textAlign: 'right',
};

const trackingRowStyle: React.CSSProperties = {
    borderBlockStart: '1px solid var(--p-color-border-secondary, #e3e3e3)',
    paddingBlockStart: 12,
};

const timelineListStyle: React.CSSProperties = {
    paddingLeft: 56,
    position: 'relative',
};

const timelineRailStyle: React.CSSProperties = {
    background: 'var(--p-color-border-secondary, #e3e3e3)',
    bottom: 0,
    left: 24,
    position: 'absolute',
    top: 4,
    width: 2,
};

const timelineItemStyle: React.CSSProperties = {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'minmax(0, 1fr) max-content',
    paddingBlockEnd: 24,
    position: 'relative',
};

const timelineMarkerStyle: React.CSSProperties = {
    alignItems: 'center',
    background: 'var(--p-color-bg-surface-secondary, #f1f1f1)',
    borderRadius: 5,
    display: 'flex',
    height: 17,
    justifyContent: 'center',
    left: -41,
    position: 'absolute',
    top: 2,
    width: 17,
};

const timelineMarkerDotStyle: React.CSSProperties = {
    background: 'var(--p-color-text-subdued, #616161)',
    borderRadius: 2,
    height: 7,
    width: 7,
};

const timelineTimeStyle: React.CSSProperties = {
    minWidth: 160,
    textAlign: 'right',
    whiteSpace: 'nowrap',
};

const lifecycleTimelineViewportStyle: React.CSSProperties = {
    overflowX: 'auto',
    paddingBlockStart: 4,
};

const lifecycleTimelineStyle = (stepCount: number): React.CSSProperties => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${Math.max(stepCount, 1)}, minmax(132px, 1fr))`,
    minWidth: stepCount > 3 ? `${stepCount * 132}px` : '100%',
});

const lifecycleStepStyle: React.CSSProperties = {
    textAlign: 'center',
};

const lifecycleMarkerRowStyle: React.CSSProperties = {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 8,
};

const lifecycleLineSpacerStyle: React.CSSProperties = {
    flex: 1,
    height: 2,
};

const isLifecycleComplete = (state?: string) => normalizeStatus(state) === 'complete';
const isLifecycleCurrent = (state?: string) => normalizeStatus(state) === 'current';

const lifecycleLineStyle = (active: boolean): React.CSSProperties => ({
    background: active ? 'var(--p-color-bg-fill-success, #29845a)' : 'var(--p-color-border, #c9cccf)',
    flex: 1,
    height: 2,
});

const lifecycleMarkerStyle = (state?: string): React.CSSProperties => {
    const normalized = normalizeStatus(state);
    const background =
        normalized === 'complete'
            ? 'var(--p-color-bg-fill-success, #29845a)'
            : normalized === 'current'
                ? 'var(--p-color-bg-fill-info, #91caff)'
                : normalized === 'error'
                    ? 'var(--p-color-bg-fill-critical, #d72c0d)'
                    : 'var(--p-color-border, #c9cccf)';

    return {
        background,
        border: '2px solid var(--p-color-bg-surface, #ffffff)',
        borderRadius: '50%',
        boxShadow: '0 0 0 1px var(--p-color-border, #c9cccf)',
        flex: '0 0 14px',
        height: 14,
        width: 14,
    };
};

const formatLifecycleStepTime = (step: AtdLifecycleStep) => {
    if (step.completedAt) {
        return formatDateTime(step.completedAt);
    }

    if (isLifecycleCurrent(step.state)) {
        return 'Current';
    }

    if (normalizeStatus(step.state) === 'error') {
        return 'Needs attention';
    }

    return 'Pending';
};

const getMerchantStatusLabel = (order: AtdShipToHomeOrderDetail) => {
    if (order.errorMessage || currentErrorStatuses.has(normalizeStatus(order.status))) {
        return order.errorMessage ? 'Action required - with errors' : 'Action required';
    }

    if (normalizeStatus(order.status) === 'fulfilled' || order.shopifyFulfilledAt) {
        return 'Fulfilled';
    }

    if (readyForPickupStatuses.has(normalizeStatus(order.status))) {
        return 'Ready for pickup';
    }

    if (shipmentStatuses.has(normalizeStatus(order.status))) {
        return order.lifecycleStep || 'Shipment in progress';
    }

    if (previewStatuses.has(normalizeStatus(order.status))) {
        return 'Order previewed';
    }

    return order.lifecycleStep || order.status || 'Unknown';
};

const OrderStatusBadge = ({ order }: { order: AtdShipToHomeOrderDetail }) => {
    const normalized = normalizeStatus(order.status);
    const label = getMerchantStatusLabel(order);

    if (order.errorMessage || currentErrorStatuses.has(normalized)) {
        return <Badge tone="critical">{label}</Badge>;
    }

    if (readyForPickupStatuses.has(normalized)) {
        return <Badge tone="success" progress="complete">{label}</Badge>;
    }

    if (normalized === 'fulfilled' || order.shopifyFulfilledAt) {
        return <Badge progress="complete">{label}</Badge>;
    }

    if (shipmentStatuses.has(normalized)) {
        return <Badge tone="info">{label}</Badge>;
    }

    if (previewStatuses.has(normalized)) {
        return <Badge progress="incomplete">{label}</Badge>;
    }

    return <Badge>{label}</Badge>;
};

const DetailRow = ({ label, children }: { label: string; children?: ReactNode }) => (
    <div style={detailRowStyle}>
        <Text as="span" tone="subdued">{label}</Text>
        <div style={detailValueStyle}>
            <Text as="span">{children === undefined || children === null || children === '' ? '-' : children}</Text>
        </div>
    </div>
);

const OptionalDetailRow = ({ label, value }: { label: string; value?: ReactNode }) => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    return <DetailRow label={label}>{value}</DetailRow>;
};

interface TrackingSummary {
    key: string;
    lineLabel: string;
    carrierName?: string;
    trackingNumber?: string;
    trackingUrl?: string;
    shipMethod?: string;
    status?: string;
    estimatedDelivery?: string;
    actualDelivery?: string;
}

const buildTrackingSummaries = (lines: AtdShipToHomeOrderLineDetail[]): TrackingSummary[] => {
    const summaries = new Map<string, TrackingSummary>();

    lines
        .filter(line => hasText(line.trackingNumber) || hasText(line.trackingUrl))
        .forEach(line => {
            const key = [
                line.carrierName || '',
                line.trackingNumber || '',
                line.trackingUrl || '',
                line.shipMethod || '',
            ].join('|');
            const lineLabel = line.title || line.sku || line.atdProductNumber || 'Product';
            const existing = summaries.get(key);

            if (existing) {
                existing.lineLabel = existing.lineLabel.includes(lineLabel)
                    ? existing.lineLabel
                    : `${existing.lineLabel}, ${lineLabel}`;
                return;
            }

            summaries.set(key, {
                key,
                lineLabel,
                carrierName: line.carrierName,
                trackingNumber: line.trackingNumber,
                trackingUrl: line.trackingUrl,
                shipMethod: line.shipMethod,
                status: line.atdFulfillmentStatus,
                estimatedDelivery: line.estimatedDelivery,
                actualDelivery: line.actualDelivery,
            });
        });

    return Array.from(summaries.values());
};

const TrackingCard = ({
    trackingSummaries,
    hasSubmittedOrder,
}: {
    trackingSummaries: TrackingSummary[];
    hasSubmittedOrder: boolean;
}) => (
    <Card>
        <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Tracking</Text>
            {trackingSummaries.length === 0 ? (
                <Text as="p" tone="subdued">
                    {hasSubmittedOrder
                        ? 'ATD has not reported tracking yet. Refresh fulfillment status to check again.'
                        : 'Tracking appears after the order is submitted to ATD and status is refreshed.'}
                </Text>
            ) : (
                <BlockStack gap="300">
                    {trackingSummaries.map((tracking, index) => (
                        <div key={tracking.key} style={index === 0 ? undefined : trackingRowStyle}>
                            <BlockStack gap="150">
                                <Text as="p" fontWeight="semibold">{tracking.lineLabel}</Text>
                                {tracking.trackingUrl ? (
                                    <Text as="p">
                                        <a href={tracking.trackingUrl} target="_blank" rel="noreferrer">
                                            {tracking.trackingNumber || 'Track shipment'}
                                        </a>
                                    </Text>
                                ) : (
                                    <Text as="p">{tracking.trackingNumber}</Text>
                                )}
                                {tracking.carrierName || tracking.shipMethod ? (
                                    <Text as="p" tone="subdued">
                                        {[tracking.carrierName, tracking.shipMethod].filter(Boolean).join(' | ')}
                                    </Text>
                                ) : null}
                                {tracking.status || tracking.estimatedDelivery || tracking.actualDelivery ? (
                                    <Text as="p" tone="subdued">
                                        {[
                                            tracking.status,
                                            tracking.estimatedDelivery ? `ETA ${formatDateTime(tracking.estimatedDelivery)}` : undefined,
                                            tracking.actualDelivery ? `Delivered ${formatDateTime(tracking.actualDelivery)}` : undefined,
                                        ].filter(Boolean).join(' | ')}
                                    </Text>
                                ) : null}
                            </BlockStack>
                        </div>
                    ))}
                </BlockStack>
            )}
        </BlockStack>
    </Card>
);

const ProductLine = ({ line }: { line: AtdShipToHomeOrderLineDetail }) => {
    const imageSource = hasText(line.imageUrl) ? line.imageUrl! : ImageIcon;
    const title = line.title || line.sku || line.atdProductNumber || 'Product';
    const identifiers = [
        line.sku ? `SKU ${line.sku}` : undefined,
        line.atdProductNumber ? `ATD ${line.atdProductNumber}` : undefined,
        line.sourceDCName || line.sourceDC ? `Source ${line.sourceDCName || line.sourceDC}` : undefined,
    ].filter(Boolean).join(' | ');
    const statusParts = [
        line.atdFulfillmentStatus || line.previewStatus,
        line.shipMethod,
        line.quantityShipped ? `${line.quantityShipped} shipped` : undefined,
        line.quantityBackordered ? `${line.quantityBackordered} backordered` : undefined,
    ].filter(Boolean).join(' | ');

    return (
        <div style={productRowStyle}>
            <Thumbnail source={imageSource} alt={title} size="large" />
            <BlockStack gap="100">
                <Text as="p" fontWeight="semibold">{title}</Text>
                {identifiers ? (
                    <Text as="p" tone="subdued">{identifiers}</Text>
                ) : null}
                {statusParts ? (
                    <Text as="p" tone="subdued">{statusParts}</Text>
                ) : null}
                {line.trackingUrl ? (
                    <Text as="p">
                        <a href={line.trackingUrl} target="_blank" rel="noreferrer">
                            {line.trackingNumber || 'Track shipment'}
                        </a>
                    </Text>
                ) : line.trackingNumber ? (
                    <Text as="p" tone="subdued">Tracking {line.trackingNumber}</Text>
                ) : null}
            </BlockStack>
            <div style={productTotalsStyle}>
                <BlockStack gap="050">
                    <Text as="p" fontWeight="semibold">{formatMoney(line.lineTotal)}</Text>
                    <Text as="p" tone="subdued">Qty {line.quantity}</Text>
                    {line.cost !== undefined && line.cost !== null && line.cost > 0 ? (
                        <Text as="p" tone="subdued">ATD cost {formatMoney(line.cost)}</Text>
                    ) : null}
                </BlockStack>
            </div>
        </div>
    );
};

interface MerchantTimelineEvent {
    id: number;
    attemptId: number;
    eventType?: string;
    message: string;
    detail?: string;
    createdAt: string;
    isPreviousAttempt?: boolean;
}

const extractMatch = (value: string | undefined, pattern: RegExp) => value?.match(pattern)?.[1]?.trim();

const getFriendlyErrorMessage = (message?: string) => {
    if (!message) {
        return 'Review the order details and retry after the issue is corrected.';
    }

    if (message.includes('does not contain a shipping address')) {
        return 'Missing shipping address. Add the customer destination in Shopify, then retry.';
    }

    const invalidProduct = extractMatch(message, /invalid-ATD-product-number:([A-Za-z0-9-]+)/i);
    if (invalidProduct) {
        return `ATD rejected product number ${invalidProduct}. Check the product mapping, then retry.`;
    }

    if (message.includes('ATD preview did not return FILLED')) {
        return 'ATD could not fill the requested order preview.';
    }

    return message.split(';')[0].trim();
};

const toMerchantTimelineEvent = (event: AtdShipToHomeOrderEventDetail): MerchantTimelineEvent | undefined => {
    const eventType = normalizeStatus(event.eventType);
    if (hiddenTimelineEventTypes.has(eventType)) {
        return undefined;
    }

    const base = {
        id: event.id,
        attemptId: event.atdShipToHomeOrderId,
        eventType: event.eventType,
        createdAt: event.createdAt,
        isPreviousAttempt: event.isPreviousAttempt,
    };

    switch (eventType) {
        case 'previewcompleted': {
            const fillKill = extractMatch(event.message, /FillKill=([^;\s]+)/i);
            return {
                ...base,
                message: fillKill?.toUpperCase() === 'FILLED'
                    ? 'ATD confirmed the order can be filled.'
                    : 'ATD could not fill the order preview.',
                detail: fillKill ? `Availability: ${fillKill}` : undefined,
            };
        }
        case 'submissioncompleted': {
            const confirmation = extractMatch(event.message, /Confirmation=([^;\s]+)/i);
            return {
                ...base,
                message: 'Order was sent to ATD.',
                detail: confirmation ? `Confirmation ${confirmation}` : undefined,
            };
        }
        case 'processingfailed':
        case 'previewfailed':
        case 'shopifyfulfillmentfailed':
            return {
                ...base,
                message: 'Action required.',
                detail: getFriendlyErrorMessage(event.message),
            };
        case 'shopifyfulfillmentcreated': {
            const tracking = extractMatch(event.message, /tracking\s+(.+?)\./i);
            return {
                ...base,
                message: 'Shopify fulfillment was created.',
                detail: tracking ? `Tracking ${tracking}` : undefined,
            };
        }
        case 'shopifyfulfillmentsynced':
            return {
                ...base,
                message: 'Shipment details were sent to Shopify.',
            };
        case 'canceleligibilitycompleted': {
            const approved = extractMatch(event.message, /Approved=(True|False)/i);
            return {
                ...base,
                message: approved?.toLowerCase() === 'true'
                    ? 'ATD says this order can be cancelled.'
                    : 'ATD says this order cannot be cancelled.',
            };
        }
        case 'cancelcompleted':
            return {
                ...base,
                message: 'Order was cancelled with ATD.',
            };
        default:
            return undefined;
    }
};

const duplicateTimelineWindowMs = 60 * 1000;

const getTimelineDedupeKey = (event: MerchantTimelineEvent) => [
    event.attemptId,
    normalizeStatus(event.eventType),
    event.message.trim().toLowerCase(),
    event.detail?.trim().toLowerCase() || '',
    event.isPreviousAttempt ? 'previous' : 'current',
].join('|');

const isNearDuplicateTimelineEvent = (event: MerchantTimelineEvent, existingEvent: MerchantTimelineEvent) => {
    if (getTimelineDedupeKey(event) !== getTimelineDedupeKey(existingEvent)) {
        return false;
    }

    return Math.abs(dayjs(event.createdAt).diff(dayjs(existingEvent.createdAt))) <= duplicateTimelineWindowMs;
};

const dedupeTimelineEvents = (events: MerchantTimelineEvent[]) => (
    events.reduce<MerchantTimelineEvent[]>((dedupedEvents, event) => {
        if (dedupedEvents.some(existingEvent => isNearDuplicateTimelineEvent(event, existingEvent))) {
            return dedupedEvents;
        }

        dedupedEvents.push(event);
        return dedupedEvents;
    }, [])
);

const EventItem = ({ event }: { event: MerchantTimelineEvent }) => (
    <div style={timelineItemStyle}>
        <span style={timelineMarkerStyle} aria-hidden="true">
            <span style={timelineMarkerDotStyle} />
        </span>
        <BlockStack gap="100">
            <Text as="p" variant="bodyMd">
                <span style={detailTextStyle}>{event.message}</span>
            </Text>
            {event.detail || event.isPreviousAttempt ? (
                <InlineStack gap="200" blockAlign="center">
                    {event.detail ? (
                        <Text as="span" variant="bodySm" tone="subdued">{event.detail}</Text>
                    ) : null}
                    {event.isPreviousAttempt ? (
                        <Text as="span" variant="bodySm" tone="subdued">Previous attempt</Text>
                    ) : null}
                </InlineStack>
            ) : null}
        </BlockStack>
        <div style={timelineTimeStyle}>
            <Text as="span" variant="bodySm" tone="subdued">{formatDateTime(event.createdAt)}</Text>
        </div>
    </div>
);

const Timeline = ({
    events,
    totalEventCount,
    expanded,
    showAllEvents,
    onToggleExpanded,
    onToggleShowAll,
}: {
    events: MerchantTimelineEvent[];
    totalEventCount: number;
    expanded: boolean;
    showAllEvents: boolean;
    onToggleExpanded: () => void;
    onToggleShowAll: () => void;
}) => (
    <Box paddingBlockStart="200">
        <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">Timeline</Text>
                <InlineStack gap="300" blockAlign="center">
                    {expanded && totalEventCount > eventPreviewCount ? (
                        <Button variant="plain" onClick={onToggleShowAll}>
                            {showAllEvents ? 'Show fewer' : `Show all ${totalEventCount}`}
                        </Button>
                    ) : null}
                    <Button variant="plain" onClick={onToggleExpanded}>
                        {expanded ? 'Hide timeline' : `Show timeline${totalEventCount ? ` (${totalEventCount})` : ''}`}
                    </Button>
                </InlineStack>
            </InlineStack>
            {expanded ? (
                totalEventCount === 0 ? (
                    <Text as="p" tone="subdued">No major timeline events recorded.</Text>
                ) : (
                    <div style={timelineListStyle}>
                        <span style={timelineRailStyle} aria-hidden="true" />
                        {events.map(event => (
                            <EventItem key={event.id} event={event} />
                        ))}
                    </div>
                )
            ) : null}
        </BlockStack>
    </Box>
);

// ATD order view. Keep the merchant-facing summary prominent and put deeper
// diagnostics in the event history so successful retries are easy to read.
const AtdOrderDetailsPage: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const orderId = Number(id);

    const [order, setOrder] = useState<AtdShipToHomeOrderDetail | undefined>();
    const [loading, setLoading] = useState(false);
    const [refreshingPreview, setRefreshingPreview] = useState(false);
    const [submittingOrder, setSubmittingOrder] = useState(false);
    const [changingFulfillmentType, setChangingFulfillmentType] = useState(false);
    const [syncingStatus, setSyncingStatus] = useState(false);
    const [checkingCancelEligibility, setCheckingCancelEligibility] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [unarchiving, setUnarchiving] = useState(false);
    const [showLifecycleDetails, setShowLifecycleDetails] = useState(false);
    const [showTimeline, setShowTimeline] = useState(true);
    const [showAllEvents, setShowAllEvents] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [notice, setNotice] = useState<string | undefined>();
    const [confirmationAction, setConfirmationAction] = useState<ConfirmationAction | undefined>();
    const [cancelEligibilityModalOpen, setCancelEligibilityModalOpen] = useState(false);
    const [cancelEligibilityResult, setCancelEligibilityResult] = useState<AtdShipToHomeCancelEligibilityResult | undefined>();
    const [cancelEligibilityError, setCancelEligibilityError] = useState<string | undefined>();

    // Detail data is loaded from the API each time the route changes or after a
    // failed retry, so the page reflects the latest stored ATD processing state.
    const loadOrder = useCallback(async () => {
        if (!Number.isFinite(orderId) || orderId <= 0) {
            setError('Invalid ATD order id.');
            return;
        }

        try {
            setLoading(true);
            setError(undefined);
            const detail = await agent.Atd.shipToHomeOrder(orderId);
            setOrder(detail);
        } catch (loadError: any) {
            setError(getErrorMessage(loadError, 'Unable to load ATD order details.'));
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        setShowLifecycleDetails(false);
        setShowTimeline(true);
        setShowAllEvents(false);
        setCancelEligibilityModalOpen(false);
        setCancelEligibilityResult(undefined);
        setCancelEligibilityError(undefined);
        setConfirmationAction(undefined);
        loadOrder();
    }, [loadOrder]);

    const requestRefreshPreview = useCallback(() => {
        if (order) setConfirmationAction('refreshPreview');
    }, [order]);

    // Refresh preview reloads live Shopify fulfillment data and stops before
    // ATD place-order. Submit is handled separately after merchant review.
    const executeRefreshPreview = useCallback(async () => {
        if (!order) return;
        try {
            setRefreshingPreview(true);
            setError(undefined);
            setNotice(undefined);
            const detail = await agent.Atd.retryShipToHomeOrder(order.id);
            setOrder(detail);
            setNotice(`Refreshed preview for ${detail.shopifyOrderName || `ATD order ${detail.id}`}.`);
        } catch (retryError: any) {
            setError(getErrorMessage(retryError, 'Unable to refresh ATD preview.'));
            await loadOrder();
        } finally {
            setRefreshingPreview(false);
            setConfirmationAction(undefined);
        }
    }, [loadOrder, order]);

    const requestSubmitOrder = useCallback(() => {
        if (order) setConfirmationAction('submitOrder');
    }, [order]);

    const executeSubmitOrder = useCallback(async () => {
        if (!order) return;
        try {
            setSubmittingOrder(true);
            setError(undefined);
            setNotice(undefined);
            const detail = await agent.Atd.submitShipToHomeOrder(order.id);
            setOrder(detail);
            setNotice(`Submitted ${detail.shopifyOrderName || `ATD order ${detail.id}`} to ATD.`);
        } catch (submitError: any) {
            setError(getErrorMessage(submitError, 'Unable to submit ATD order.'));
            await loadOrder();
        } finally {
            setSubmittingOrder(false);
            setConfirmationAction(undefined);
        }
    }, [loadOrder, order]);

    const handleFulfillmentTypeChange = useCallback(async (fulfillmentType: string) => {
        if (!order || normalizeStatus(order.fulfillmentType) === normalizeStatus(fulfillmentType)) {
            return;
        }

        if (normalizeStatus(fulfillmentType) === 'shiptohome' && isPickupDeliveryOrder(order)) {
            setNotice(undefined);
            setError('Shopify pickup orders do not include a customer shipping address, so they must use ATD Ship-To-Store.');
            return;
        }

        try {
            setChangingFulfillmentType(true);
            setError(undefined);
            setNotice(undefined);
            const detail = await agent.Atd.changeShipToHomeOrderFulfillmentType(order.id, fulfillmentType);
            setOrder(detail);
            setNotice(`Refreshed ${detail.shopifyOrderName || `ATD order ${detail.id}`} as ${formatFulfillmentType(detail)}.`);
        } catch (changeError: any) {
            setError(getErrorMessage(changeError, 'Unable to change ATD fulfillment type.'));
            await loadOrder();
        } finally {
            setChangingFulfillmentType(false);
        }
    }, [loadOrder, order]);

    const handleSyncStatus = useCallback(async () => {
        if (!order) {
            return;
        }

        try {
            setSyncingStatus(true);
            setError(undefined);
            setNotice(undefined);
            // Pull latest shipment/tracking details from ATD and let the backend
            // push any newly shipped quantity into Shopify.
            const detail = await agent.Atd.syncShipToHomeOrderStatus(order.id);
            setOrder(detail);
            setNotice(`Refreshed fulfillment status for ${detail.shopifyOrderName || `ATD order ${detail.id}`}.`);
        } catch (syncError: any) {
            setError(getErrorMessage(syncError, 'Unable to refresh ATD fulfillment status.'));
            await loadOrder();
        } finally {
            setSyncingStatus(false);
        }
    }, [loadOrder, order]);

    const handleCheckCancelEligibility = useCallback(async () => {
        if (!order) {
            return;
        }

        try {
            setCheckingCancelEligibility(true);
            setCancelEligibilityModalOpen(true);
            setCancelEligibilityResult(undefined);
            setCancelEligibilityError(undefined);
            setError(undefined);
            setNotice(undefined);
            const response = await agent.Atd.checkShipToHomeCancelEligibility(order.id);
            setCancelEligibilityResult(response);
            await loadOrder();
        } catch (cancelError: any) {
            setCancelEligibilityError(getErrorMessage(cancelError, 'Unable to check ATD cancel eligibility.'));
            await loadOrder();
        } finally {
            setCheckingCancelEligibility(false);
        }
    }, [loadOrder, order]);

    const handleCloseCancelEligibilityModal = useCallback(() => {
        if (checkingCancelEligibility || cancelling) {
            return;
        }

        setCancelEligibilityModalOpen(false);
        setCancelEligibilityResult(undefined);
        setCancelEligibilityError(undefined);
    }, [cancelling, checkingCancelEligibility]);

    const handleCancel = useCallback(async () => {
        if (!order || cancelEligibilityResult?.approved !== true) {
            return;
        }

        try {
            setCancelling(true);
            setError(undefined);
            setNotice(undefined);
            setCancelEligibilityError(undefined);
            const detail = await agent.Atd.cancelShipToHomeOrder(order.id);
            setOrder(detail);
            setCancelEligibilityModalOpen(false);
            setCancelEligibilityResult(undefined);
            setNotice(`Cancelled ${detail.shopifyOrderName || `ATD order ${detail.id}`} with ATD.`);
        } catch (cancelError: any) {
            setCancelEligibilityResult(undefined);
            setCancelEligibilityError(getErrorMessage(cancelError, 'Unable to cancel ATD order.'));
            await loadOrder();
        } finally {
            setCancelling(false);
        }
    }, [cancelEligibilityResult?.approved, loadOrder, order]);

    const requestArchive = useCallback(() => {
        if (order) setConfirmationAction('archive');
    }, [order]);

    const executeArchive = useCallback(async () => {
        if (!order) return;
        try {
            setArchiving(true);
            setError(undefined);
            setNotice(undefined);
            const detail = await agent.Atd.archiveShipToHomeOrder(order.id);
            setOrder(detail);
            setNotice(`Archived ${detail.shopifyOrderName || `ATD order ${detail.id}`}.`);
        } catch (archiveError: any) {
            setError(getErrorMessage(archiveError, 'Unable to archive ATD order.'));
            await loadOrder();
        } finally {
            setArchiving(false);
            setConfirmationAction(undefined);
        }
    }, [loadOrder, order]);

    const handleUnarchive = useCallback(async () => {
        if (!order) {
            return;
        }

        try {
            setUnarchiving(true);
            setError(undefined);
            setNotice(undefined);
            const detail = await agent.Atd.unarchiveShipToHomeOrder(order.id);
            setOrder(detail);
            setNotice(`Restored ${detail.shopifyOrderName || `ATD order ${detail.id}`} to the active list.`);
        } catch (archiveError: any) {
            setError(getErrorMessage(archiveError, 'Unable to restore ATD order.'));
            await loadOrder();
        } finally {
            setUnarchiving(false);
        }
    }, [loadOrder, order]);

    const timelineEvents = useMemo(() => {
        if (!order) {
            return [];
        }

        return dedupeTimelineEvents(order.events
            .map(toMerchantTimelineEvent)
            .filter((event): event is MerchantTimelineEvent => Boolean(event)));
    }, [order]);

    const visibleEvents = useMemo(() => (
        showAllEvents ? timelineEvents : timelineEvents.slice(0, eventPreviewCount)
    ), [showAllEvents, timelineEvents]);
    // ATD reports tracking at fulfillment-line level. Group repeated tracking
    // numbers so a split order does not show the same shipment several times.
    const trackingSummaries = useMemo(() => (
        order ? buildTrackingSummaries(order.lines) : []
    ), [order]);

    const title = order?.shopifyOrderName || (order ? `ATD order ${order.id}` : 'ATD order');
    const shopHandle = getShopifyStoreHandle();
    const shopifyOrderLabel = order?.shopifyOrderName || (order?.shopifyOrderId ? String(order.shopifyOrderId) : '-');
    const shopifyAdminOrderUrl = shopHandle && order?.shopifyOrderId
        ? `https://admin.shopify.com/store/${shopHandle}/orders/${order.shopifyOrderId}`
        : '';
    const actionInProgress = refreshingPreview || submittingOrder || changingFulfillmentType || syncingStatus || checkingCancelEligibility || cancelling || archiving || unarchiving;
    const confirmationActionLoading =
        (confirmationAction === 'refreshPreview' && refreshingPreview) ||
        (confirmationAction === 'submitOrder' && submittingOrder) ||
        (confirmationAction === 'archive' && archiving);
    const confirmationModalTitle =
        confirmationAction === 'submitOrder'
            ? 'Submit preview to ATD'
            : confirmationAction === 'archive'
                ? 'Archive ATD order'
                : 'Refresh ATD preview';
    const confirmationModalBody =
        confirmationAction === 'submitOrder'
            ? 'Submit this filled ATD preview now? This will place the order with ATD.'
            : confirmationAction === 'archive'
                ? 'Archive this ATD order from the active list? You can restore it later from archived orders.'
                : 'Refresh this ATD preview from the latest Shopify fulfillment data?';
    const confirmationModalPrimaryAction =
        confirmationAction === 'submitOrder'
            ? executeSubmitOrder
            : confirmationAction === 'archive'
                ? executeArchive
                : executeRefreshPreview;
    const handleCloseConfirmationModal = () => {
        if (!confirmationActionLoading) {
            setConfirmationAction(undefined);
        }
    };
    const secondaryActions = [
        // When a preview is valid, Submit becomes the primary action and Refresh
        // remains available as a secondary action in case Shopify data changed.
        ...(order?.canSubmit && order?.canRetry ? [{
            content: 'Refresh preview',
            onAction: requestRefreshPreview,
            loading: refreshingPreview,
            disabled: actionInProgress && !refreshingPreview,
            icon: RefreshIcon,
        }] : []),
        ...(order?.canSyncStatus ? [{
            content: 'Refresh fulfillment status',
            onAction: handleSyncStatus,
            loading: syncingStatus,
            disabled: actionInProgress && !syncingStatus,
            icon: RefreshIcon,
        }] : []),
        ...(order?.canCheckCancelEligibility ? [{
            content: 'Check cancel eligibility',
            onAction: handleCheckCancelEligibility,
            loading: checkingCancelEligibility,
            disabled: actionInProgress && !checkingCancelEligibility,
        }] : []),
        ...(order?.archivedAt ? [{
            content: 'Restore',
            onAction: handleUnarchive,
            loading: unarchiving,
            disabled: actionInProgress && !unarchiving,
        }] : order?.canArchive ? [{
            content: 'Archive',
            onAction: requestArchive,
            loading: archiving,
            disabled: actionInProgress && !archiving,
        }] : []),
    ];

    return (
        <Page
            title={title}
            subtitle={order?.atdOrderNumbers ? `ATD order ${order.atdOrderNumbers}` : order?.atdConfirmationNumber ? `ATD confirmation ${order.atdConfirmationNumber}` : undefined}
            backAction={{ content: 'ATD orders', onAction: () => navigate('/integrations/atd/orders') }}
            primaryAction={order?.canSubmit ? {
                content: 'Submit to ATD',
                onAction: requestSubmitOrder,
                loading: submittingOrder,
                disabled: actionInProgress && !submittingOrder,
            } : order?.canRetry ? {
                content: 'Refresh preview',
                onAction: requestRefreshPreview,
                loading: refreshingPreview,
                disabled: actionInProgress && !refreshingPreview,
            } : undefined}
            secondaryActions={secondaryActions}
        >
            <Modal
                open={Boolean(confirmationAction)}
                onClose={handleCloseConfirmationModal}
                title={confirmationModalTitle}
                primaryAction={{
                    content: confirmationAction === 'submitOrder'
                        ? 'Submit to ATD'
                        : confirmationAction === 'archive'
                            ? 'Archive order'
                            : 'Refresh preview',
                    destructive: confirmationAction === 'archive',
                    onAction: confirmationModalPrimaryAction,
                    loading: confirmationActionLoading,
                    disabled: confirmationActionLoading,
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: handleCloseConfirmationModal,
                    disabled: confirmationActionLoading,
                }]}
            >
                <Modal.Section>
                    <Text as="p">{confirmationModalBody}</Text>
                </Modal.Section>
            </Modal>

            <Modal
                open={cancelEligibilityModalOpen}
                onClose={handleCloseCancelEligibilityModal}
                title="Cancel ATD order"
                primaryAction={cancelEligibilityResult?.approved ? {
                    content: 'Cancel ATD order',
                    destructive: true,
                    onAction: handleCancel,
                    loading: cancelling,
                    disabled: checkingCancelEligibility || cancelling,
                } : {
                    content: 'Close',
                    onAction: handleCloseCancelEligibilityModal,
                    disabled: checkingCancelEligibility || cancelling,
                }}
                secondaryActions={cancelEligibilityResult?.approved ? [{
                    content: 'Do not cancel',
                    onAction: handleCloseCancelEligibilityModal,
                    disabled: checkingCancelEligibility || cancelling,
                }] : undefined}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        {checkingCancelEligibility ? (
                            <InlineStack gap="200" blockAlign="center">
                                <Spinner accessibilityLabel="Checking ATD cancel eligibility" size="small" />
                                <Text as="p">Checking ATD cancel eligibility...</Text>
                            </InlineStack>
                        ) : null}

                        {cancelEligibilityError ? (
                            <Banner tone="critical" title="Cancellation check failed">
                                <p>{cancelEligibilityError}</p>
                            </Banner>
                        ) : null}

                        {!checkingCancelEligibility && cancelEligibilityResult ? (
                            cancelEligibilityResult.approved ? (
                                <Banner tone="warning" title="Cancellation is currently eligible">
                                    <p>{cancelEligibilityResult.message || 'ATD reports this order is eligible for cancellation.'}</p>
                                    <p>Confirm cancellation now only if you want to cancel this ATD order.</p>
                                </Banner>
                            ) : (
                                <Banner tone="critical" title="Cancellation is not eligible">
                                    <p>{cancelEligibilityResult.message || 'ATD reports this order is not eligible for cancellation.'}</p>
                                </Banner>
                            )
                        ) : null}

                        {!checkingCancelEligibility && !cancelEligibilityResult && !cancelEligibilityError ? (
                            <Text as="p" tone="subdued">Waiting for ATD cancel eligibility.</Text>
                        ) : null}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <Layout>
                <Layout.Section>
                    <BlockStack gap="400">
                        {error ? (
                            <Banner tone="critical" title="ATD action failed">
                                <p>{error}</p>
                            </Banner>
                        ) : null}

                        {notice ? (
                            <Banner tone="success" title="ATD action completed">
                                <p>{notice}</p>
                            </Banner>
                        ) : null}

                        {loading && !order ? (
                            <Card>
                                <Text as="p">Loading ATD order...</Text>
                            </Card>
                        ) : null}

                        {order ? (
                            <>
                                {order.errorMessage ? (
                                    <Banner tone="critical" title="Current error">
                                        <p style={detailTextStyle}>{order.errorMessage}</p>
                                    </Banner>
                                ) : null}

                                {order.statusMessages ? (
                                    <Banner tone="info" title="ATD messages">
                                        <p style={detailTextStyle}>{order.statusMessages}</p>
                                    </Banner>
                                ) : null}

                                <Layout>
                                    <Layout.Section>
                                        <BlockStack gap="400">
                                            {isPreviewApprovalPending(order) ? (
                                                <Card>
                                                    <BlockStack gap="300">
                                                        <BlockStack gap="150">
                                                            {/* Header row is full width so the fulfillment mode pins to
                                                            the right edge instead of following the copy block width. */}
                                                            <div style={previewHeaderRowStyle}>
                                                                <Badge tone="success">Preview ready</Badge>
                                                                <Text as="span" tone="subdued">{formatFulfillmentType(order)}</Text>
                                                            </div>
                                                            <Text as="h2" variant="headingMd">Review and submit to ATD</Text>
                                                            <Text as="p" tone="subdued">
                                                                ATD filled the preview. Confirm the totals and fulfillment mode before sending the order.
                                                            </Text>
                                                        </BlockStack>
                                                        <InlineStack>
                                                            <Button
                                                                variant="primary"
                                                                onClick={requestSubmitOrder}
                                                                loading={submittingOrder}
                                                                disabled={actionInProgress && !submittingOrder}
                                                            >
                                                                Submit to ATD
                                                            </Button>
                                                        </InlineStack>
                                                        <Divider />
                                                        <InlineStack gap="600" wrap>
                                                            <BlockStack gap="050">
                                                                <Text as="span" tone="subdued">Total</Text>
                                                                <Text as="p" variant="headingLg">{formatMoney(order.previewOrderTotal)}</Text>
                                                            </BlockStack>
                                                            <BlockStack gap="050">
                                                                <Text as="span" tone="subdued">Freight</Text>
                                                                <Text as="p" variant="headingLg">{formatMoney(order.previewFreightTotal)}</Text>
                                                            </BlockStack>
                                                            <BlockStack gap="050">
                                                                <Text as="span" tone="subdued">Availability</Text>
                                                                <Text as="p" variant="headingMd">{order.fillKillStatus || '-'}</Text>
                                                            </BlockStack>
                                                            <BlockStack gap="050">
                                                                <Text as="span" tone="subdued">Reference</Text>
                                                                <Text as="p" variant="bodyMd">{order.atdSubmissionReference || '-'}</Text>
                                                            </BlockStack>
                                                        </InlineStack>
                                                    </BlockStack>
                                                </Card>
                                            ) : null}

                                            <Card>
                                                <BlockStack gap="300">
                                                    <InlineStack align="space-between" blockAlign="center" gap="300">
                                                        <BlockStack gap="100">
                                                            <Text as="h2" variant="headingMd">ATD Fulfillment Status</Text>
                                                            <InlineStack gap="200" blockAlign="center">
                                                                <span style={statusBadgeWrapStyle}>
                                                                    <OrderStatusBadge order={order} />
                                                                </span>
                                                                <Text as="span" tone="subdued">
                                                                    Updated {formatDateTime(order.updatedAt || order.lastStatusSyncAt || order.createdAt)}
                                                                </Text>
                                                            </InlineStack>
                                                        </BlockStack>
                                                        <Button variant="plain" onClick={() => setShowLifecycleDetails(value => !value)}>
                                                            {showLifecycleDetails ? 'Hide steps' : 'Show steps'}
                                                        </Button>
                                                    </InlineStack>
                                                    {!isCancelledOrder(order) ? (
                                                        <ProgressBar progress={order.lifecycleProgressPercent || 0} size="small" />
                                                    ) : null}
                                                    {showLifecycleDetails ? (
                                                        <div style={lifecycleTimelineViewportStyle}>
                                                            <div style={lifecycleTimelineStyle(order.lifecycleSteps.length)}>
                                                                {order.lifecycleSteps.map((step, index) => {
                                                                    const previousStep = order.lifecycleSteps[index - 1];
                                                                    const isLastStep = index === order.lifecycleSteps.length - 1;

                                                                    return (
                                                                        <div key={step.key} style={lifecycleStepStyle}>
                                                                            <div style={lifecycleMarkerRowStyle}>
                                                                                {index === 0 ? (
                                                                                    <span style={lifecycleLineSpacerStyle} />
                                                                                ) : (
                                                                                    <span style={lifecycleLineStyle(isLifecycleComplete(previousStep?.state))} />
                                                                                )}
                                                                                <span style={lifecycleMarkerStyle(step.state)} />
                                                                                {isLastStep ? (
                                                                                    <span style={lifecycleLineSpacerStyle} />
                                                                                ) : (
                                                                                    <span style={lifecycleLineStyle(isLifecycleComplete(step.state))} />
                                                                                )}
                                                                            </div>
                                                                            <BlockStack gap="050">
                                                                                <Text as="p" variant="bodySm" fontWeight={isLifecycleCurrent(step.state) ? 'semibold' : undefined}>
                                                                                    {step.label}
                                                                                </Text>
                                                                                <Text as="p" variant="bodySm" tone="subdued">
                                                                                    {formatLifecycleStepTime(step)}
                                                                                </Text>
                                                                            </BlockStack>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </BlockStack>
                                            </Card>

                                            <Card>
                                                <BlockStack gap="300">
                                                    <Text as="h2" variant="headingMd">Products</Text>
                                                    {order.lines.length === 0 ? (
                                                        <Text as="p" tone="subdued">No line items recorded.</Text>
                                                    ) : (
                                                        <BlockStack gap="300">
                                                            {order.lines.map((line, index) => (
                                                                <Box key={line.id}>
                                                                    <ProductLine line={line} />
                                                                    {index < order.lines.length - 1 ? (
                                                                        <Box paddingBlockStart="300">
                                                                            <Divider />
                                                                        </Box>
                                                                    ) : null}
                                                                </Box>
                                                            ))}
                                                        </BlockStack>
                                                    )}
                                                </BlockStack>
                                            </Card>

                                            <Timeline
                                                events={visibleEvents}
                                                totalEventCount={timelineEvents.length}
                                                expanded={showTimeline}
                                                showAllEvents={showAllEvents}
                                                onToggleExpanded={() => setShowTimeline(value => !value)}
                                                onToggleShowAll={() => setShowAllEvents(value => !value)}
                                            />
                                        </BlockStack>
                                    </Layout.Section>

                                    <Layout.Section variant="oneThird">
                                        <BlockStack gap="400">
                                            <Card>
                                                <BlockStack gap="300">
                                                    <Text as="h2" variant="headingMd">Order details</Text>
                                                    <div style={financialSummaryGridStyle}>
                                                        <BlockStack gap="050">
                                                            <Text as="span" tone="subdued">Total</Text>
                                                            <Text as="p" variant="headingLg">{formatMoney(order.previewOrderTotal)}</Text>
                                                        </BlockStack>
                                                        <BlockStack gap="050">
                                                            <Text as="span" tone="subdued">Freight</Text>
                                                            <Text as="p" variant="headingLg">{formatMoney(order.previewFreightTotal)}</Text>
                                                        </BlockStack>
                                                    </div>

                                                    <Divider />

                                                    <BlockStack gap="200">
                                                        <DetailRow label="Shopify order">
                                                            {shopifyAdminOrderUrl ? (
                                                                <Button url={shopifyAdminOrderUrl} target="_blank" variant="plain">
                                                                    {shopifyOrderLabel}
                                                                </Button>
                                                            ) : (
                                                                shopifyOrderLabel
                                                            )}
                                                        </DetailRow>
                                                        {order.canChangeFulfillmentType ? (
                                                            <>
                                                                {/* Switching STH/STS is not a cosmetic local edit. The backend
                                                                rebuilds the ATD preview because the two modes use different
                                                                ATD endpoints and destination payloads. */}
                                                                <Select
                                                                    label="Fulfillment"
                                                                    options={getFulfillmentTypeOptions(order)}
                                                                    value={isShipToStoreOrder(order) ? 'ShipToStore' : 'ShipToHome'}
                                                                    onChange={handleFulfillmentTypeChange}
                                                                    disabled={actionInProgress || changingFulfillmentType}
                                                                />
                                                                {isPickupDeliveryOrder(order) ? (
                                                                    <Text as="p" variant="bodySm" tone="subdued">
                                                                        Shopify pickup orders can only use ATD Ship-To-Store unless Shopify provides a customer shipping address.
                                                                    </Text>
                                                                ) : null}
                                                            </>
                                                        ) : (
                                                            <DetailRow label="Fulfillment">{formatFulfillmentType(order)}</DetailRow>
                                                        )}
                                                        <OptionalDetailRow label="Shopify delivery" value={order.shopifyDeliveryMethodType} />
                                                        <OptionalDetailRow label="Availability" value={order.fillKillStatus} />
                                                        {isShipToStoreOrder(order) ? (
                                                            <OptionalDetailRow label="STS options" value={formatShipToStoreOptions(order)} />
                                                        ) : null}
                                                    </BlockStack>

                                                    <Divider />

                                                    <BlockStack gap="200">
                                                        <OptionalDetailRow label="ATD reference" value={order.atdSubmissionReference} />
                                                        <OptionalDetailRow label="ATD location" value={order.atdLocationNumber} />
                                                        <OptionalDetailRow label="ATD order" value={order.atdOrderNumbers} />
                                                        <OptionalDetailRow label="ATD confirmation" value={order.atdConfirmationNumber} />
                                                        <OptionalDetailRow label="ATD status" value={order.atdOrderStatus} />
                                                        <OptionalDetailRow label="Invoice" value={order.atdInvoiceNumbers} />
                                                    </BlockStack>

                                                    <Divider />

                                                    <BlockStack gap="200">
                                                        <OptionalDetailRow label="Submitted" value={order.submittedAt ? formatDateTime(order.submittedAt) : undefined} />
                                                        <OptionalDetailRow label="Status refreshed" value={order.lastStatusSyncAt ? formatDateTime(order.lastStatusSyncAt) : undefined} />
                                                        <OptionalDetailRow
                                                            label={isShipToStoreOrder(order) ? 'Shopify pickup' : 'Shopify fulfilled'}
                                                            value={order.shopifyFulfilledAt ? formatDateTime(order.shopifyFulfilledAt) : undefined}
                                                        />
                                                        <OptionalDetailRow
                                                            label="Last eligibility check"
                                                            value={order.cancelEligibilityCheckedAt
                                                                ? `${order.cancelEligibilityApproved === true ? 'Approved' : order.cancelEligibilityApproved === false ? 'Not approved' : 'Unknown'} (${formatDateTime(order.cancelEligibilityCheckedAt)})`
                                                                : undefined}
                                                        />
                                                        <OptionalDetailRow label="Cancelled" value={order.cancelledAt ? formatDateTime(order.cancelledAt) : undefined} />
                                                        <OptionalDetailRow label="Archived" value={order.archivedAt ? formatDateTime(order.archivedAt) : undefined} />
                                                    </BlockStack>
                                                </BlockStack>
                                            </Card>

                                            <TrackingCard
                                                trackingSummaries={trackingSummaries}
                                                hasSubmittedOrder={Boolean(order.atdConfirmationNumber || order.submittedAt)}
                                            />

                                            <Card>
                                                <BlockStack gap="300">
                                                    <Text as="h2" variant="headingMd">{isShipToStoreOrder(order) ? 'Store pickup location' : 'Destination'}</Text>
                                                    <BlockStack gap="100">
                                                        <Text as="p" fontWeight="semibold">{order.shipToName || '-'}</Text>
                                                        <Text as="p">{order.shipToAddressSummary || '-'}</Text>
                                                        {order.customerEmail ? (
                                                            <Text as="p" tone="subdued">{order.customerEmail}</Text>
                                                        ) : null}
                                                        {order.shipToPhone ? (
                                                            <Text as="p" tone="subdued">{order.shipToPhone}</Text>
                                                        ) : null}
                                                    </BlockStack>
                                                </BlockStack>
                                            </Card>
                                        </BlockStack>
                                    </Layout.Section>
                                </Layout>
                            </>
                        ) : null}
                    </BlockStack>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default AtdOrderDetailsPage;
